import { useState, useEffect } from 'react';
import { WeekendSettingsResponse, UpdateWeekendSettingsRequest } from '../types/billing-calendar';
import api from '../lib/api';
import { toast } from './use-toast';

interface UseWeekendSettingsProps {
  clientId: number;
}

export const useWeekendSettings = ({ clientId }: UseWeekendSettingsProps) => {
  const [settings, setSettings] = useState<WeekendSettingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar configurações
  const loadSettings = async () => {
    if (!clientId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{ success: boolean; data: WeekendSettingsResponse }>(
        `/billing-rules/weekend-settings/${clientId}`
      );

      if (response.success && response.data) {
        setSettings(response.data);
      } else {
        throw new Error('Erro ao carregar configurações');
      }
    } catch (err: any) {
      console.error('❌ [useWeekendSettings] Erro ao carregar configurações:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao carregar configurações');
      toast({
        title: 'Erro',
        description: 'Erro ao carregar configurações de dias não úteis',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Salvar configurações
  const saveSettings = async (newSettings: UpdateWeekendSettingsRequest) => {
    if (!clientId) return false;

    setSaving(true);
    setError(null);

    try {
      const response = await api.put<{ success: boolean; data: WeekendSettingsResponse; message?: string }>(
        `/billing-rules/weekend-settings/${clientId}`,
        newSettings
      );

      if (response.success && response.data) {
        setSettings(response.data);
        toast({
          title: 'Sucesso',
          description: response.message || 'Configurações salvas com sucesso',
          variant: 'default'
        });
        return true;
      } else {
        throw new Error('Erro ao salvar configurações');
      }
    } catch (err: any) {
      console.error('❌ [useWeekendSettings] Erro ao salvar configurações:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Erro ao salvar configurações';
      setError(errorMsg);
      toast({
        title: 'Erro',
        description: errorMsg,
        variant: 'destructive'
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Carregar ao montar ou quando clientId mudar
  useEffect(() => {
    if (clientId) {
      loadSettings();
    }
  }, [clientId]);

  return {
    settings,
    loading,
    saving,
    error,
    loadSettings,
    saveSettings
  };
};

